import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Locate, Loader2, MapPin, Star, Bike, UtensilsCrossed, ArrowRight, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { mediaUrl } from "../lib/mediaUrl";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import SEOHead from "../components/SEOHead";
import OpenFastBadge from "../components/OpenFastBadge";

// Page d'accueil Nold Go — inspirée Deliveroo (fond blanc/crème, hero 3 colonnes desktop).
// Bloc central (texte + recherche) encadré par 2×2 photos alléchantes de nourriture.
// Rangée de vignettes cuisines cliquables sous la recherche.
// Carrousel auto-scroll de partenaires entre le hero et la grille filtrée.

const GROCERY_LEFT = "/nold-grocery-bag.png"; // sac courses IA généré (Nano Banana)
const GROCERY_LEFT_WEBP = "/nold-grocery-bag.webp"; // version WebP (perf mobile / Core Web Vitals)
const FOODS_RIGHT = [
  "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=600&q=75&fm=webp", // burger
  "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?auto=format&fit=crop&w=600&q=75&fm=webp", // sushi
];

// Catégories de cuisine — chaque entrée : { key, label, icon, matches: [keywords lowercase] }.
// La correspondance est effectuée sur le champ `r.cuisine` (regex insensible à la casse).
const CUISINE_TABS = [
  { key: "all",       label: "Tous",       icon: "🍽️", matches: null },
  { key: "burgers",   label: "Burgers",    icon: "🍔", matches: ["burger", "smash"] },
  { key: "pizza",     label: "Pizza",      icon: "🍕", matches: ["pizza"] },
  { key: "sushi",     label: "Sushi",      icon: "🍣", matches: ["sushi", "japonais", "japonaise", "japan"] },
  { key: "italien",   label: "Italien",    icon: "🍝", matches: ["italien", "italienne", "pasta", "pâte", "trattoria"] },
  { key: "asiatique", label: "Asiatique",  icon: "🥡", matches: ["asiat", "chinois", "chinoise", "thai", "thaï", "viet", "coréen", "coreen", "wok"] },
  { key: "africain",  label: "Africain",   icon: "🌍", matches: ["africain", "africaine", "cameroun", "sénégal", "senegal", "maroc", "marocain", "tunisien", "ivoirien", "congolais"] },
  { key: "desserts",  label: "Desserts",   icon: "🍰", matches: ["dessert", "pâtisserie", "patisserie", "sucré", "glace", "boulangerie"] },
];

function RestoCard({ r }) {
  const hasDistance = typeof r.distance_km === "number";
  const etaMin = typeof r.delivery_eta_min === "number" ? r.delivery_eta_min : 30;
  return (
    <Link
      to={`/restaurants/${r.id}`}
      data-testid={`home-resto-card-${r.id}`}
      className="group bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden hover:border-[#059669] hover:shadow-xl transition-all"
    >
      <div className="aspect-[16/10] bg-gradient-to-br from-[#F0EBE1] to-[#E2DFD8] overflow-hidden relative">
        {r.cover_url ? (
          <img
            src={mediaUrl(r.cover_url)} alt={r.name}
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#75807A]">
            <UtensilsCrossed className="w-10 h-10" />
          </div>
        )}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#059669] text-white text-[10px] font-black uppercase tracking-wider shadow-md">
          <Bike className="w-3 h-3" /> Livraison
        </div>
        {/* Badge FOMO "Ouvert & rapide" (bas-droit) — remplace le badge "km" quand pertinent. */}
        <div className="absolute bottom-3 right-3">
          <OpenFastBadge resto={r} />
        </div>
        {r.cuisine && (
          <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#059669] text-xs font-bold shadow-sm">
            {r.cuisine}
          </div>
        )}
        {hasDistance && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#1A1A1A] text-[11px] font-bold shadow-sm">
            <MapPin className="w-3 h-3 text-[#059669]" /> {r.distance_km.toFixed(1)} km
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-head font-bold text-[#1A1A1A] text-lg truncate">{r.name}</h3>
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#059669] whitespace-nowrap">
            <Star className="w-3.5 h-3.5 fill-[#059669]" /> 4.6
          </span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-[#75807A]">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{r.commune || r.address}</span>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-[#75807A]">Livraison ~{etaMin} min · Frais 2,50 €</span>
          <span className="inline-flex items-center gap-1 font-bold text-[#059669] group-hover:translate-x-1 transition-transform">
            Commander <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// Vignette carrousel plus compacte que RestoCard — utilisée dans la bande auto-scroll.
function PartnerTile({ r }) {
  return (
    <Link
      to={`/restaurants/${r.id}`}
      data-testid={`home-partner-tile-${r.id}`}
      className="shrink-0 w-64 rounded-2xl overflow-hidden bg-white border border-[#E2DFD8] hover:border-[#059669] hover:shadow-lg transition-all group"
    >
      <div className="aspect-[16/10] bg-[#F0EBE1] relative overflow-hidden">
        {r.cover_url ? (
          <img src={mediaUrl(r.cover_url)} alt={r.name}
               className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
               loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#75807A]">
            <UtensilsCrossed className="w-8 h-8" />
          </div>
        )}
        {r.cuisine && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-white/95 backdrop-blur text-[#059669] text-[10px] font-bold shadow-sm">
            {r.cuisine}
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="font-head font-bold text-sm text-[#1A1A1A] truncate">{r.name}</div>
        <div className="mt-0.5 text-[11px] text-[#75807A] truncate">{r.commune || r.address || "Bruxelles"}</div>
      </div>
    </Link>
  );
}

// Carrousel infini : liste dupliquée + CSS keyframes translateX 0 → -50%.

// ---- Carte Leaflet du suivi livreur ----
const RESTAURANT_ICON = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-100%)"><div style="width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#F26419;box-shadow:0 4px 12px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;border:3px solid #fff"><span style="transform:rotate(45deg);font-size:16px">🍽️</span></div></div>`,
  iconSize: [36, 36], iconAnchor: [0, 0],
});
const DESTINATION_ICON = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-100%);position:relative"><div style="position:absolute;left:0;top:8px;width:36px;height:36px;border-radius:50%;background:#059669;opacity:.25;animation:nold-mappulse 1.8s ease-in-out infinite"></div><div style="position:relative;width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#059669;box-shadow:0 4px 12px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;border:3px solid #fff"><span style="transform:rotate(45deg);font-size:16px">🏠</span></div></div>`,
  iconSize: [36, 36], iconAnchor: [0, 0],
});
const RIDER_ICON = L.divIcon({
  className: "",
  html: `<div style="transform:translate(-50%,-50%)"><div style="width:44px;height:44px;border-radius:50%;background:#fff;border:3px solid #059669;box-shadow:0 4px 14px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center"><span style="font-size:22px">🚴</span></div></div>`,
  iconSize: [44, 44], iconAnchor: [0, 0],
});
const RESTAURANT_POS = [50.8542, 4.3488];
const DESTINATION_POS = [50.8305, 4.3702];
const RIDE_PATH = [
  [50.8542, 4.3488], [50.8500, 4.3520], [50.8460, 4.3560],
  [50.8420, 4.3600], [50.8380, 4.3640], [50.8340, 4.3680], [50.8305, 4.3702],
];
const BRUSSELS_CENTER = [50.8423, 4.3595];

function LiveTrackingIllustration() {
  const [pos, setPos] = useState(RIDE_PATH[0]);
  const rafRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const totalMs = 9000;
    const segments = RIDE_PATH.length - 1;
    const t0 = performance.now();
    const step = (now) => {
      if (!alive) return;
      const t = ((now - t0) % totalMs) / totalMs;
      const raw = Math.floor(t * segments);
      const seg = raw >= segments ? segments - 1 : raw < 0 ? 0 : raw;
      const local = Math.max(0, Math.min(1, t * segments - seg));
      const a = RIDE_PATH[seg]; const b = RIDE_PATH[seg + 1];
      if (a && b) setPos([a[0] + (b[0] - a[0]) * local, a[1] + (b[1] - a[1]) * local]);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { alive = false; if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);
  return (
    <div className="relative w-full aspect-[16/10] max-w-xl mx-auto rounded-3xl overflow-hidden shadow-lg border border-[#059669]/20" data-testid="tracking-illu">
      <MapContainer center={BRUSSELS_CENTER} zoom={13} scrollWheelZoom={false} dragging={false} doubleClickZoom={false} touchZoom={false} zoomControl={false} attributionControl={false} style={{ width: "100%", height: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
        <Marker position={RESTAURANT_POS} icon={RESTAURANT_ICON} />
        <Marker position={DESTINATION_POS} icon={DESTINATION_ICON} />
        <Marker position={pos} icon={RIDER_ICON} />
      </MapContainer>
      <div className="absolute top-3 right-3 z-[400] bg-white rounded-full px-3 py-1.5 shadow-md border border-[#059669]/20 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#059669] animate-pulse" />
        <span className="text-xs font-bold text-[#1A1A1A]">Arrivée dans 8 min</span>
      </div>
      <div className="absolute bottom-1 right-2 z-[400] text-[9px] text-[#75807A] bg-white/80 rounded px-1">© OpenStreetMap</div>
      <style>{`@keyframes nold-mappulse { 0%,100% { transform: scale(1); opacity: 0.25; } 50% { transform: scale(1.9); opacity: 0.05; } }`}</style>
    </div>
  );
}

function PartnersCarousel({ items, reverse = false, title = "Nos partenaires Nold Go" }) {
  if (!items || items.length === 0) return null;
  // On duplique la liste pour créer l'effet de boucle sans saut visible.
  const loop = [...items, ...items];
  const cls = reverse ? "nold-marquee-reverse" : "nold-marquee";
  return (
    <section className="border-y border-[#EEE9E0] bg-[#FAF8F3] py-6 overflow-hidden" data-testid={reverse ? "home-partners-carousel-2" : "home-partners-carousel"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4 flex items-baseline justify-between">
        <h3 className="font-head font-black text-lg sm:text-xl text-[#1A1A1A]" dangerouslySetInnerHTML={{ __html: title }} />
        <span className="text-xs text-[#75807A]">Défilement continu · Cliquez pour voir le menu</span>
      </div>
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#FAF8F3] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#FAF8F3] to-transparent z-10" />
        <div className={`flex gap-4 ${cls}`}>
          {loop.map((r, idx) => (<PartnerTile key={`${r.id}-${idx}`} r={r} />))}
        </div>
      </div>
      <style>{`
        .nold-marquee { width: max-content; animation: nold-marq 45s linear infinite; }
        .nold-marquee-reverse { width: max-content; animation: nold-marq-rev 55s linear infinite; }
        .nold-marquee:hover, .nold-marquee-reverse:hover { animation-play-state: paused; }
        @keyframes nold-marq { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes nold-marq-rev { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) { .nold-marquee, .nold-marquee-reverse { animation: none; } }
      `}</style>
    </section>
  );
}

export default function HomeAddress() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [addr, setAddr] = useState("");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [restos, setRestos] = useState([]);
  const [loadingRestos, setLoadingRestos] = useState(true);
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    try {
      const raw = localStorage.getItem("nold_home_addr");
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.addr) setAddr(saved.addr);
        if (saved?.coords?.lat && saved?.coords?.lng) setCoords(saved.coords);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRestos(true);
      try {
        const r = await api.get(`/restaurants?limit=48`).then((x) => x.data);
        if (!cancelled) setRestos(r.items || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoadingRestos(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filtre client-side selon l'onglet cuisine actif.
  const filteredRestos = useMemo(() => {
    const tab = CUISINE_TABS.find((c) => c.key === activeCat);
    if (!tab || !tab.matches) return restos;
    return restos.filter((r) => {
      const c = String(r.cuisine || "").toLowerCase();
      return tab.matches.some((kw) => c.includes(kw));
    });
  }, [restos, activeCat]);

  const goToList = (nextAddr, nextCoords) => {
    try { localStorage.setItem("nold_home_addr", JSON.stringify({ addr: nextAddr, coords: nextCoords || null })); } catch { /* ignore */ }
    const params = new URLSearchParams({ addr: nextAddr });
    if (nextCoords?.lat && nextCoords?.lng) {
      params.set("lat", String(nextCoords.lat));
      params.set("lng", String(nextCoords.lng));
    }
    navigate(`/restaurants?${params.toString()}`);
  };

  const search = () => {
    const a = addr.trim();
    if (a.length < 3) { toast.info("Entrez votre adresse pour continuer."); return; }
    goToList(a, coords);
  };

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) { toast.error("Géolocalisation non disponible sur ce navigateur."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=18&addressdetails=1`,
            { headers: { "Accept-Language": "fr" } }
          );
          const d = await r.json();
          const display = d?.display_name || `Ma position (${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)})`;
          const nextCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setAddr(display); setCoords(nextCoords);
          toast.success("Position détectée ✓");
          goToList(display, nextCoords);
        } catch { toast.error("Impossible de récupérer votre adresse."); }
        finally { setLocating(false); }
      },
      (err) => {
        setLocating(false);
        toast.error(err?.code === 1 ? "Permission de localisation refusée." : "Position introuvable.");
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  };

  const onKeyDown = (e) => { if (e.key === "Enter") search(); };

  return (
    <div className="min-h-screen bg-white overflow-x-clip" data-testid="home-address-page">
      <SEOHead
        title="Nold Go — Livraison de repas & VTC à Bruxelles"
        description="Commandez vos repas préférés livrés à vélo à Bruxelles et réservez un VTC en quelques secondes. Livraison rapide, restaurants locaux, paiement sécurisé."
        canonical="/"
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://www.noldgo.com/#org",
              name: "Nold Go",
              url: "https://www.noldgo.com",
              logo: "https://www.noldgo.com/nold-logo.png",
              sameAs: [],
              address: {
                "@type": "PostalAddress",
                streetAddress: "Rue Jourdan 71 A0",
                postalCode: "1060",
                addressLocality: "Saint-Gilles",
                addressCountry: "BE",
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://www.noldgo.com/#website",
              url: "https://www.noldgo.com",
              name: "Nold Go",
              publisher: { "@id": "https://www.noldgo.com/#org" },
              potentialAction: {
                "@type": "SearchAction",
                target: "https://www.noldgo.com/restaurants?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "Service",
              name: "Livraison de repas à Bruxelles",
              provider: { "@id": "https://www.noldgo.com/#org" },
              areaServed: { "@type": "AdministrativeArea", name: "Bruxelles-Capitale" },
              description: "Service de livraison de repas à vélo à Bruxelles — restaurants partenaires vérifiés, livraison en 30 minutes, paiement carte sécurisé.",
            },
            {
              "@type": "FAQPage",
              mainEntity: [
                {
                  "@type": "Question",
                  name: "Où Nold Go livre-t-il ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Nold Go livre dans toute la Région de Bruxelles-Capitale : Bruxelles-Ville, Ixelles, Saint-Gilles, Etterbeek, Uccle, Anderlecht, Schaerbeek, Forest et les Woluwe.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Combien coûte la livraison ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Les frais de livraison sont fixes à 2,50 € par commande, avec un minimum de commande de 15 €. Aucun frais caché.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Quels moyens de paiement sont acceptés ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "Paiement 100 % en ligne par carte bancaire (Visa, Mastercard, Bancontact) via Stripe, ou par Google Pay et Apple Pay.",
                  },
                },
                {
                  "@type": "Question",
                  name: "Combien de temps prend une livraison ?",
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: "En moyenne 25 à 45 minutes selon la distance et le restaurant. Le suivi temps réel est disponible dans l'application.",
                  },
                },
              ],
            },
          ],
        }}
      />
      {/* NAVBAR blanche : logo à gauche · CTAs à droite */}
      <header className="border-b border-[#EEE9E0] bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="home-logo">
            <img src="/nold-logo.png" alt="Nold Go" className="h-8 w-8 sm:h-9 sm:w-9 object-contain" loading="eager" />
            <span className="font-head font-black text-base sm:text-lg text-[#1A1A1A] leading-none hidden xs:inline sm:inline">
              Nold <span className="text-[#059669]">Go</span>
            </span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <Link to="/covoiturage" data-testid="home-nav-carpool"
              className="inline-flex h-9 sm:h-10 px-2 sm:px-4 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#FFF7ED] font-bold text-xs sm:text-sm items-center gap-1 transition-colors">
              🚗<span className="hidden sm:inline">Covoiturage</span>
            </Link>
            {user ? (
              <Link to="/mes-commandes" data-testid="home-nav-orders"
                className="h-9 sm:h-10 px-2 sm:px-4 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-xs sm:text-sm inline-flex items-center transition-colors">
                <span className="hidden sm:inline">Mes commandes</span><span className="sm:hidden">Cmd.</span>
              </Link>
            ) : (
              <>
                <Link to="/auth" data-testid="home-nav-login"
                  className="h-9 sm:h-10 px-2 sm:px-4 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-xs sm:text-sm inline-flex items-center transition-colors whitespace-nowrap">
                  <span className="hidden sm:inline">Se connecter</span><span className="sm:hidden">Connexion</span>
                </Link>
                <Link to="/auth?intent=signup" data-testid="home-nav-signup"
                  className="hidden xs:inline-flex h-9 sm:h-10 px-2 sm:px-4 rounded-full bg-white border-2 border-[#059669] text-[#059669] hover:bg-[#F0FDF4] font-bold text-xs sm:text-sm items-center transition-colors whitespace-nowrap">
                  <span className="hidden sm:inline">S'inscrire</span><span className="sm:hidden">Inscription</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO 3 colonnes : 2 photos gauche · texte + recherche + cuisines centrés · 2 photos droite */}
      <section className="relative bg-[#FAF8F3]">
        {/* Colonne gauche : UNE grande photo d'un sac de courses Nold Go bien rempli, avec le logo Nold Go
            superposé sur le sac (le logo est appliqué en CSS pour rester net et éditable). */}
        <div className="absolute inset-y-0 left-0 w-1/4 hidden lg:block p-1">
          <div className="relative w-full h-full overflow-hidden rounded-tr-3xl rounded-br-3xl bg-white">
            <picture>
              <source srcSet={GROCERY_LEFT_WEBP} type="image/webp" />
              <img src={GROCERY_LEFT} alt="Sac de courses Nold Go" className="w-full h-full object-contain" loading="eager" decoding="async" fetchpriority="high" width="480" height="720" />
            </picture>
            {/* Overlay logo Nold Go — bas du sac, aligné au centre horizontal */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-[15%] flex items-center gap-2 pointer-events-none">
              <img src="/nold-logo.png" alt="" className="h-10 w-10 object-contain drop-shadow" loading="eager" decoding="async" width="40" height="40" />
              <span className="font-head font-black text-2xl text-[#059669] drop-shadow-sm">
                Nold <span className="text-[#F26419]">Go</span>
              </span>
            </div>
          </div>
        </div>
        {/* Colonne photos droite (2 photos empilées) */}
        <div className="absolute inset-y-0 right-0 w-1/4 hidden lg:grid grid-rows-2 gap-1 p-1">
          {FOODS_RIGHT.map((src, i) => (
            <div key={i} className="relative overflow-hidden rounded-tl-3xl first:rounded-bl-3xl">
              <img src={src} alt="" className="w-full h-full object-cover" loading="eager" decoding="async" width="600" height="400" />
            </div>
          ))}
        </div>

        {/* Contenu central */}
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-24 text-center" data-testid="home-hero">
          {/* Bandeau visuel MOBILE — visible uniquement < lg. Reprend les 3 mêmes photos que sur desktop. */}
          <div className="lg:hidden mb-8 grid grid-cols-3 gap-2" data-testid="home-hero-mobile-visuals">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#F0EBE1]">
              <picture>
                <source srcSet={GROCERY_LEFT_WEBP} type="image/webp" />
                <img src={GROCERY_LEFT} alt="Sac de courses Nold Go" className="w-full h-full object-contain" loading="eager" decoding="async" fetchpriority="high" width="300" height="300" />
              </picture>
              <div className="absolute left-1/2 -translate-x-1/2 bottom-2 flex items-center gap-1 pointer-events-none">
                <img src="/nold-logo.png" alt="" className="h-4 w-4 object-contain drop-shadow" loading="eager" decoding="async" width="16" height="16" />
                <span className="font-head font-black text-[10px] text-[#059669] drop-shadow-sm">
                  Nold <span className="text-[#F26419]">Go</span>
                </span>
              </div>
            </div>
            {FOODS_RIGHT.slice(0, 2).map((src, i) => (
              <div key={i} className="relative aspect-square rounded-2xl overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" loading="eager" decoding="async" width="300" height="300" />
              </div>
            ))}
          </div>

          <h1 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.1]" data-testid="home-slogan">
            Vos plats préférés,<br className="hidden sm:inline" /> livrés chez vous
          </h1>
          <p className="mt-4 text-[#75807A] text-base sm:text-lg">
            Entrez une adresse pour découvrir notre sélection.
          </p>

          <div className="mt-8 max-w-xl mx-auto bg-white rounded-2xl border border-[#E2DFD8] shadow-sm p-2 flex flex-col sm:flex-row gap-2" data-testid="home-address-box" onKeyDown={onKeyDown}>
            <div className="flex-1 min-w-0">
              <AddressAutocomplete
                value={addr}
                onChange={(v) => { setAddr(v); if (!v) setCoords(null); }}
                onSelect={(r) => { setCoords({ lat: r.lat, lng: r.lng }); }}
                placeholder="Saisissez votre adresse"
                country="be" testid="home-address-input" dotColor="#059669"
              />
            </div>
            <button data-testid="home-address-search" onClick={search}
              className="h-14 px-6 rounded-2xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-base inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0 transition-colors">
              <Search className="w-5 h-5" /> Chercher
            </button>
          </div>

          <button data-testid="home-use-location" onClick={useMyLocation} disabled={locating}
            className="mt-4 inline-flex items-center gap-1.5 text-[#75807A] hover:text-[#059669] text-sm font-semibold disabled:opacity-60 transition-colors">
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4" />}
            {locating ? "Détection en cours…" : "Utiliser ma position"}
          </button>

          {/* Sélection catégories retirée de l'accueil — accessible depuis /restaurants */}
        </div>
      </section>

      {/* CARROUSEL 1 — auto-scroll gauche */}
      {!loadingRestos && restos.length > 0 && (
        <PartnersCarousel items={restos} title={'Nos partenaires <span class="text-[#059669]">Nold Go</span>'} />
      )}

      {/* CARROUSEL 2 — auto-scroll droite (sens inverse pour dynamiser la page) */}
      {!loadingRestos && restos.length > 0 && (
        <PartnersCarousel items={[...restos].reverse()} reverse title={'Découvrez aussi <span class="text-[#F26419]">nos autres restaurants</span>'} />
      )}
      {/* Ancien bloc PartnersCarousel unique remplacé ci-dessus. */}

      {/* La grille des vraies vitrines vit sur /restaurants — pas sur l'accueil. */}

      {/* SECTION Suivez votre livreur en temps réel — Leaflet + OSM */}
      <section className="bg-white py-16 sm:py-20" data-testid="home-tracking-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F0FDF4] border border-[#059669]/30 text-[#059669] text-xs font-bold uppercase tracking-widest">
              <MapPin className="w-3.5 h-3.5" /> Suivi en direct
            </div>
            <h2 className="mt-4 font-head font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.1]">
              Suivez votre livreur <span className="text-[#059669]">en temps réel</span>
            </h2>
            <p className="mt-4 text-[#3B423E] text-base sm:text-lg">
              Dès votre commande passée, une carte interactive vous montre exactement où se trouve votre livreur, minute par minute.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-[#3B423E]">
              <li className="flex items-start gap-3"><span className="mt-1 w-2 h-2 rounded-full bg-[#059669] shrink-0" />Position mise à jour toutes les secondes</li>
              <li className="flex items-start gap-3"><span className="mt-1 w-2 h-2 rounded-full bg-[#059669] shrink-0" />Heure d'arrivée estimée précise</li>
              <li className="flex items-start gap-3"><span className="mt-1 w-2 h-2 rounded-full bg-[#059669] shrink-0" />Contact direct avec le livreur si besoin</li>
            </ul>
          </div>
          <div className="order-1 lg:order-2"><LiveTrackingIllustration /></div>
        </div>
      </section>

      {/* Bande chiffres clés */}
      <section className="bg-white py-10 border-y border-[#EEE9E0]" data-testid="home-stats">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 grid grid-cols-2 gap-4 sm:gap-8 text-center">
          <div>
            <div className="font-head font-black text-3xl sm:text-4xl text-[#059669]">~30 min</div>
            <div className="mt-1 text-xs sm:text-sm text-[#75807A] font-semibold">Temps de livraison moyen</div>
          </div>
          <div className="border-l border-[#EEE9E0]">
            <div className="font-head font-black text-3xl sm:text-4xl text-[#059669]">Bruxelles</div>
            <div className="mt-1 text-xs sm:text-sm text-[#75807A] font-semibold">& communes environnantes</div>
          </div>
        </div>
      </section>

      {/* SECTION CTA Restaurateur avec photos cliquables */}
      <section className="bg-gradient-to-br from-[#059669] via-[#047857] to-[#065F46] text-white py-20 sm:py-24" data-testid="home-cta-restaurateur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-xs font-bold uppercase tracking-widest">
            <ChefHat className="w-3.5 h-3.5" /> Partenaires restaurateurs
          </div>
          <h2 className="mt-5 font-head font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Rejoignez <span className="text-[#FDE68A]">Nold Go</span>
          </h2>
          <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">
            Développez votre chiffre d'affaires en livrant à Bruxelles. Activation unique 195 €, aucun abonnement mensuel, commission juste à 25 %.
          </p>
          {restos.length > 0 && (
            <div className="mt-10 flex items-center justify-center" data-testid="home-cta-partners-row">
              <div className="flex -space-x-3 sm:-space-x-4">
                {restos.filter(r => r.cover_url).slice(0, 7).map((r, i) => (
                  <Link key={r.id} to={`/restaurants/${r.id}`} title={r.name} data-testid={`home-cta-partner-${r.id}`}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden border-2 border-white shadow-lg bg-white/20 backdrop-blur transition-transform hover:-translate-y-1 hover:z-20 focus:z-20 focus:outline-none focus:ring-4 focus:ring-white/40"
                    style={{ zIndex: 10 - i }}>
                    <img src={mediaUrl(r.cover_url)} alt={r.name} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </Link>
                ))}
                {restos.length > 7 && (
                  <Link to="/restaurants" data-testid="home-cta-partner-more"
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 border-white shadow-lg bg-[#FDE68A] text-[#065F46] flex items-center justify-center font-head font-black text-sm hover:-translate-y-1 hover:z-20 transition-transform focus:outline-none focus:ring-4 focus:ring-white/40">
                    +{restos.length - 7}
                  </Link>
                )}
              </div>
            </div>
          )}
          {restos.length > 0 && <p className="mt-3 text-xs sm:text-sm text-white/80 font-semibold">Ils nous font déjà confiance</p>}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/restaurateur/inscription" data-testid="home-cta-restaurateur-btn"
              className="h-14 px-8 rounded-full bg-white text-[#059669] hover:bg-[#FDE68A] hover:text-[#065F46] font-bold text-base inline-flex items-center gap-2 shadow-xl transition-colors">
              Devenir partenaire <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:contact@noldgo.com" data-testid="home-cta-restaurateur-contact"
              className="h-14 px-8 rounded-full bg-transparent border-2 border-white/60 text-white hover:bg-white/10 font-bold text-base inline-flex items-center gap-2 transition-colors">
              Nous contacter
            </a>
          </div>
        </div>
      </section>

      {/* OUTIL DE LOCALISATION EN BAS — façon Deliveroo */}
      <section className="bg-[#FAF8F3] border-t border-[#EEE9E0] py-14 sm:py-16" data-testid="home-bottom-address">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#059669]/30 text-[#059669] text-xs font-bold uppercase tracking-widest">
            <MapPin className="w-3.5 h-3.5" /> Livraison à Bruxelles
          </div>
          <h2 className="mt-4 font-head font-black text-3xl sm:text-4xl text-[#1A1A1A] leading-[1.05]">Prêt à commander ?</h2>
          <p className="mt-3 text-[#75807A] text-base">Entrez votre adresse pour voir les restaurants qui livrent chez vous.</p>
          <div className="mt-6 bg-white rounded-2xl border border-[#E2DFD8] shadow-sm p-2 flex flex-col sm:flex-row gap-2" onKeyDown={onKeyDown}>
            <div className="flex-1 min-w-0">
              <AddressAutocomplete value={addr} onChange={(v) => { setAddr(v); if (!v) setCoords(null); }}
                onSelect={(r) => { setCoords({ lat: r.lat, lng: r.lng }); }}
                placeholder="Saisissez votre adresse" country="be" testid="home-bottom-address-input" dotColor="#059669" />
            </div>
            <button data-testid="home-bottom-address-search" onClick={search}
              className="h-14 px-6 rounded-2xl bg-[#059669] hover:bg-[#047857] text-white font-bold text-base inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0 transition-colors">
              <Search className="w-5 h-5" /> Chercher
            </button>
          </div>
          <button data-testid="home-bottom-use-location" onClick={useMyLocation} disabled={locating}
            className="mt-3 inline-flex items-center gap-1.5 text-[#75807A] hover:text-[#059669] text-sm font-semibold disabled:opacity-60 transition-colors">
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Locate className="w-4 h-4" />}
            {locating ? "Détection en cours…" : "Utiliser ma position"}
          </button>
        </div>
      </section>

      <div data-testid="home-carpool-links" className="bg-white border-t border-[#EEE9E0] py-5">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
          <span className="font-bold text-[#1A1A1A]">Covoiturage :</span>
          <Link to="/covoiturage/bruxelles-paris" data-testid="home-footer-carpool-bxl-paris" className="text-[#75807A] hover:text-[#059669] transition-colors">Bruxelles – Paris</Link>
          <Link to="/covoiturage/paris-bruxelles" data-testid="home-footer-carpool-paris-bxl" className="text-[#75807A] hover:text-[#059669] transition-colors">Paris – Bruxelles</Link>
          <Link to="/covoiturage/decouvrir" data-testid="home-footer-carpool-discover" className="text-[#75807A] hover:text-[#059669] transition-colors">Découvrir le covoiturage</Link>
        </div>
      </div>
    </div>
  );
}
